import { useCallback } from 'react';
import { Temporal } from 'temporal-polyfill';
import { useDialogHandlers } from '~/global-state/dialog';
import { useAsyncOperationsAtom } from './atoms';

/**
 * Consumers rendered inside the virtualized operations list must use this hook
 * rather than useAsyncOperations to avoid unnecessary re-renders of the entire list
 */
export function useEventWarnings() {
  const [asyncOperations, setAsyncOperations] = useAsyncOperationsAtom();
  const { handleCloseDialog } = useDialogHandlers('AsyncOperationsDialog');

  const handleSetEventIdsWithWarnings = useCallback(
    (eventIdsWithWarnings: readonly string[]) => {
      setAsyncOperations((prev) => {
        const seen = new Set(prev.eventIdsWithWarnings);
        const newEventIdsWithWarnings = eventIdsWithWarnings.filter((eventIdWithWarning) => {
          if (seen.has(eventIdWithWarning)) {
            return false;
          }

          seen.add(eventIdWithWarning);
          return true;
        });

        if (newEventIdsWithWarnings.length === 0) {
          return prev;
        }

        return {
          ...prev,
          eventIdsWithWarnings: [...prev.eventIdsWithWarnings, ...newEventIdsWithWarnings],
        };
      });
    },
    [setAsyncOperations],
  );

  const handleSetEventIdWithWarning = useCallback(
    (eventIdWithWarning: string) => {
      handleSetEventIdsWithWarnings([eventIdWithWarning]);
    },
    [handleSetEventIdsWithWarnings],
  );

  const handleRemoveEventIdWithWarning = useCallback(
    (eventIdWithWarning: string) => {
      setAsyncOperations((prev) => ({
        ...prev,
        eventIdsWithWarnings: prev.eventIdsWithWarnings.filter((id) => id !== eventIdWithWarning),
      }));
    },
    [setAsyncOperations],
  );

  const handleClearAllWarnings = useCallback(() => {
    setAsyncOperations((prev) => ({ ...prev, eventIdsWithWarnings: [] }));
  }, [setAsyncOperations]);

  const handlePanelClosed = useCallback(() => {
    setAsyncOperations((prev) => ({ ...prev, panelLastClosedAt: Temporal.Now.instant(), isPanelOpen: false }));
    handleCloseDialog();
  }, [setAsyncOperations, handleCloseDialog]);

  return {
    eventIdsWithWarnings: asyncOperations.eventIdsWithWarnings,
    handleSetEventIdsWithWarnings,
    handleSetEventIdWithWarning,
    handleRemoveEventIdWithWarning,
    handleClearAllWarnings,
    handlePanelClosed,
  };
}
